import { TakeAwayPrinterSetupTabProps } from '@/renderer/types/printer';
import { useAppSelector } from '@hooks/useRedux';
import { selectEffectiveOutlet } from '@store/outlets';
import { Monitor } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ONBOARDING_TOUR_TARGETS } from '../../../services/onboardingTourService';
import { Button, DropdownSelect } from '../../common';

export const TakeAwayPrintersTab: React.FC<TakeAwayPrinterSetupTabProps> = ({
  billPrinterId,
  canManage = true,
  departments,
  departmentPrinterMap,
  deviceId,
  isSaving,
  onSubmit,
  outletId,
  printerOptions,
}) => {
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const [selectedBillPrinterId, setSelectedBillPrinterId] = useState('');
  const [departmentSelections, setDepartmentSelections] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setSelectedBillPrinterId(billPrinterId || '');
    setDepartmentSelections(departmentPrinterMap || {});
  }, [billPrinterId, departmentPrinterMap]);

  const hasPrinters = printerOptions.length > 0;
  const hasDepartments = departments.length > 0;
  const allDepartmentsConfigured = departments.every((department) =>
    Boolean(departmentSelections[department.id]),
  );
  const isConfigurationComplete =
    Boolean(selectedBillPrinterId) &&
    hasDepartments &&
    allDepartmentsConfigured &&
    Boolean(deviceId);

  const handleDepartmentPrinterChange = (
    departmentId: string,
    printerId: string,
  ) => {
    setDepartmentSelections((previous) => ({
      ...previous,
      [departmentId]: printerId,
    }));
  };

  const handleSubmit = async () => {
    if (!deviceId || !canManage) return;
    if (!isConfigurationComplete) return;

    await onSubmit({
      outlet_id: outletId,
      device_id: deviceId,
      bill_printer_id: selectedBillPrinterId || null,
      department_mappings: departments
        .filter((department) => Boolean(departmentSelections[department.id]))
        .map((department) => ({
          department_id: department.id,
          printer_id: departmentSelections[department.id],
        })),
    });
  };

  return (
    <div
      className="space-y-4"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.takeawayPage}
    >
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <Monitor size={16} className="text-gray-500" />
          <h2 className="text-base font-semibold uppercase tracking-wide text-black">
            Takeaway Printer Setup
          </h2>
        </div>

        <p className="text-xs text-gray-600">
          Configure department KOT and bill printers for sectionless orders on
          this device.
        </p>

        <div className="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
          Outlet: {selectedOutlet?.name || '—'}
        </div>

        <div className="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
          Device ID: {deviceId || 'Unavailable'}
        </div>
      </div>

      {!hasPrinters ? (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-5 text-center text-sm text-gray-600">
          Add at least one printer before configuring takeaway printer routing.
        </div>
      ) : null}

      {!deviceId ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Device identity is not ready yet. Refresh and try again.
        </div>
      ) : null}

      <div className="space-y-2">
        {departments.map((department) => (
          <div
            key={department.id}
            className="grid grid-cols-[1fr_260px] items-center gap-4 rounded border border-gray-200 bg-white px-4 py-3"
          >
            <span className="font-medium uppercase tracking-wide text-black">
              {department.name}
            </span>
            <DropdownSelect
              value={departmentSelections[department.id] || ''}
              onChange={(v) => handleDepartmentPrinterChange(department.id, v)}
              options={[{ value: '', label: 'No printer' }, ...printerOptions]}
              disabled={!hasPrinters || !deviceId || !canManage}
            />
          </div>
        ))}

        <div className="grid grid-cols-[1fr_260px] items-center gap-4 rounded border border-gray-200 bg-white px-4 py-3">
          <span className="font-medium uppercase tracking-wide text-black">
            Bill
          </span>
          <DropdownSelect
            value={selectedBillPrinterId}
            onChange={setSelectedBillPrinterId}
            options={[{ value: '', label: 'No printer' }, ...printerOptions]}
            disabled={!hasPrinters || !deviceId || !canManage}
          />
        </div>

        {!canManage ? (
          <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            You can view this printer setup, but only authorized roles can
            change it.
          </div>
        ) : null}
        {!hasDepartments ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            No active departments found for this outlet. Add departments first.
          </div>
        ) : null}

        {hasDepartments && !isConfigurationComplete ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Select a bill printer and a printer for every department before
            saving.
          </div>
        ) : null}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          isLoading={isSaving}
          data-onboarding-tour={ONBOARDING_TOUR_TARGETS.takeawaySaveButton}
          disabled={
            !hasPrinters ||
            !hasDepartments ||
            !deviceId ||
            !isConfigurationComplete ||
            !canManage
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
};
